
import { Vue, Options } from 'vue-class-component'
import { maska } from 'maska'
import { Prop } from 'vue-property-decorator'
import FlexMessage from '@/components/common/flex-message/FlexMessage.vue'
import {
  IFlexMessageBubble,
  IFlexMessageButton,
  IFlexMessageCarousel,
  IFlexMessageIcon,
  IFlexMessageImage,
  IFlexMessageSpan,
  IFlexMessageText,
} from '@/utils/types'
import cloneDeep from 'lodash/cloneDeep'
import TemplateButtons from '@/components/common/flex-message/functions/TemplateButtons.vue'
import HelperButtons from '@/components/common/flex-message/functions/HelperButtons.vue'

@Options({
  components: {
    HelperButtons,
    TemplateButtons,
    FlexMessage,
  },
  directives: { maska },
  emits: ['update:modelValue', 'initMessage', 'update:hoverPath', 'onSelectNode', 'update:altText', 'captureMessage'],
})
export default class PreviewPanel extends Vue {
  @Prop()
  modelValue!: IFlexMessageBubble | IFlexMessageCarousel

  @Prop()
  hoverPath!: string

  @Prop()
  isChanged!: boolean

  @Prop()
  isAdmin!: boolean

  @Prop()
  isSuper!: boolean

  @Prop()
  errorPath!: string[]

  @Prop()
  altText!: string

  selectedNode: IFlexMessageButton | IFlexMessageIcon | IFlexMessageImage | IFlexMessageSpan | IFlexMessageText = {}
  selectedPath = ''
  listNodePath: string[] = []
  componentKeyBubble = 0
  componentKeyCarousel = 0
  imgFile: File | null = null
  previewImageUrl = ''
  action = 'new'

  get value() {
    return this.modelValue
  }

  set value(value: IFlexMessageBubble | IFlexMessageCarousel) {
    this.$emit('update:modelValue', value)
  }

  get currentHovePath(): string {
    return this.hoverPath
  }

  set currentHovePath(value: string) {
    this.$emit('update:hoverPath', value)
  }

  get nodePath() {
    if (this.selectedPath === '') return []
    const keys = this.selectedPath.replace(/(box|text)-(\d+)/g, 'contents.$2').split('.')

    if (this.modelValue) {
      if (this.modelValue.type === 'bubble') {
        keys.shift()
      } else {
        const firstKey = keys.shift()
        if (firstKey) {
          keys.unshift('contents', firstKey)
        } else {
          keys.unshift('contents')
        }
      }
    }
    return keys
  }

  initMessage(message) {
    this.value = cloneDeep(message)
    this.listNodePath = []
    this.componentKeyBubble++
    this.componentKeyCarousel++
    this.$emit('initMessage', message)
    if (!this.isAdmin) {
      this.$nextTick(() => {
        this.$refs.helperButtons.initMessage(message)
      })
    }
  }

  onSelectNode(node, path) {
    if (this.errorPath.length > 0) return
    this.selectedPath = path
    if (path === '') {
      this.listNodePath = []
      this.componentKeyBubble++
      this.componentKeyCarousel++
    }
    if (node) {
      this.selectedNode = node
    } else {
      this.selectedNode = this.getNodeValue()
    }
    this.$emit('onSelectNode', this.selectedPath, this.selectedNode, this.nodePath)
  }

  onFlipNode(newIndex) {
    this.selectedPath = this.selectedPath.replace(/(\D)(\d+)(?=\.[a-zA-Z]+$)/, `$1${newIndex}`)
    this.onResetNode()
  }

  onResetNode() {
    this.listNodePath = []
    this.componentKeyBubble++
    this.componentKeyCarousel++
    this.$emit('onSelectNode', this.selectedPath, this.selectedNode, this.nodePath)
  }

  initListNode(path) {
    this.listNodePath = path
  }

  getNodeValue() {
    const keys = cloneDeep(this.nodePath)
    const lastKey = keys[keys.length - 1]
    if (this.value.type === 'bubble') {
      if (lastKey === undefined) {
        return this.value
      } else if (!['header', 'hero', 'body', 'footer'].includes(lastKey)) {
        keys.pop()
      }
    } else {
      if (lastKey === 'carousel') {
        return this.value
      } else if (['box', 'image', 'text', 'button', 'filler', 'separator', 'video', 'span', 'icon'].includes(lastKey)) {
        keys.pop()
      }
    }
    if (['header', 'hero', 'body', 'footer'].includes(lastKey)) {
      if (this.value.type !== 'bubble') {
        keys.pop()
      }
    }
    return keys.reduce((acc, key) => acc && acc[key], this.value)
  }

  captureMessage() {
    this.$refs.flexMessage.captureMessage()
  }

  getMessageImage(file, url) {
    this.imgFile = file
    this.previewImageUrl = url
    this.$emit('captureMessage', this.imgFile, this.previewImageUrl)
  }

  mounted() {
    if (this.value && !this.isAdmin) {
      this.$nextTick(() => {
        this.$refs.helperButtons.initMessage(this.value)
      })
    }
  }
}
